import { useState } from "react";
import { MapPin, Clock, Mail, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanVisit = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    visit_date: "",
    service_time: "",
    kids_info: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Combine kids_info and service_time into message
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        visit_date: formData.visit_date,
        message: `Service Time: ${formData.service_time}\nChildren: ${formData.kids_info || 'None'}`,
      };
      
      await axios.post(`${API}/contact`, submissionData);
      toast.success("Thank you! A Visit Success Manager will reach out soon.");
      setFormData({ name: "", email: "", phone: "", message: "", visit_date: "", service_time: "", kids_info: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceTimeChange = (value) => {
    setFormData({ ...formData, service_time: value });
  };

  return (
    <section id="visit" className="section-spacing bg-white" data-testid="plan-visit-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: Info */}
          <div>
            <p className="text-overline mb-4" data-testid="visit-overline">Plan Your Visit</p>
            <div className="accent-bar mb-8" />
            <h2 className="heading-massive text-4xl sm:text-5xl text-black mb-8" data-testid="visit-heading">
              We can't<br />
              wait to<br />
              meet you!
            </h2>
            <p className="font-['Satoshi'] text-lg text-zinc-600 leading-relaxed mb-12" data-testid="visit-description">
              Fill out the form below, and a <strong className="text-black">Visit Success Manager</strong> will reach out to help guide you on your first Sunday.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4" data-testid="visit-location">
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} className="text-black" />
                </div>
                <div>
                  <h4 className="font-['Clash_Display'] font-semibold text-lg mb-1">Location</h4>
                  <p className="font-['Satoshi'] text-zinc-600">
                    8900 Hayne Boulevard<br />
                    New Orleans, Louisiana
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="visit-times">
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <Clock size={24} className="text-black" />
                </div>
                <div>
                  <h4 className="font-['Clash_Display'] font-semibold text-lg mb-1">Service Times</h4>
                  <p className="font-['Satoshi'] text-zinc-600">
                    Sunday: 8:00 AM & 11:00 AM<br />
                    Wednesday: 7:00 PM
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="visit-contact">
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <Mail size={24} className="text-black" />
                </div>
                <div>
                  <h4 className="font-['Clash_Display'] font-semibold text-lg mb-1">Contact</h4>
                  <p className="font-['Satoshi'] text-zinc-600">
                    lightcitychurchnola@gmail.com<br />
                    (504) 603-8249
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="card-hard-shadow bg-zinc-50 p-8 md:p-12" data-testid="visit-form-container">
            <h3 className="font-['Clash_Display'] font-semibold text-2xl mb-2">
              Plan My Visit
            </h3>
            <p className="font-['Satoshi'] text-zinc-600 mb-6">
              Let us know you're coming so we can prepare for your arrival.
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="visit-form">
              <div>
                <label className="font-['Satoshi'] font-medium text-sm mb-2 block">
                  Full Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-sharp h-12"
                  placeholder="John Doe"
                  data-testid="visit-form-name"
                />
              </div>

              <div>
                <label className="font-['Satoshi'] font-medium text-sm mb-2 block">
                  Email Address *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-sharp h-12"
                  placeholder="john@example.com"
                  data-testid="visit-form-email"
                />
              </div>

              <div>
                <label className="font-['Satoshi'] font-medium text-sm mb-2 block">
                  Which Sunday are you joining us? *
                </label>
                <Input
                  type="date"
                  name="visit_date"
                  value={formData.visit_date}
                  onChange={handleChange}
                  required
                  className="input-sharp h-12"
                  data-testid="visit-form-date"
                />
              </div>

              <div>
                <label className="font-['Satoshi'] font-medium text-sm mb-2 block">
                  Service Time *
                </label>
                <Select onValueChange={handleServiceTimeChange} value={formData.service_time}>
                  <SelectTrigger className="input-sharp h-12" data-testid="visit-form-service-time">
                    <SelectValue placeholder="Select a service time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8:00 AM - Leadership Training">8:00 AM - Leadership Training</SelectItem>
                    <SelectItem value="11:00 AM - Worship Encounter">11:00 AM - Worship Encounter</SelectItem>
                    <SelectItem value="Wednesday 7:00 PM - Bible Training">Wednesday 7:00 PM - Bible Training</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-['Satoshi'] font-medium text-sm mb-2 block">
                  Bringing children? (Ages/Count)
                </label>
                <Textarea
                  name="kids_info"
                  value={formData.kids_info}
                  onChange={handleChange}
                  className="input-sharp min-h-[80px]"
                  placeholder="e.g. Two kids, ages 5 and 8"
                  data-testid="visit-form-kids"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-sharp bg-black text-white hover:bg-white hover:text-black hover:shadow-[inset_0_0_0_2px_black] px-8 py-6 text-sm w-full flex items-center justify-center gap-2"
                data-testid="visit-form-submit"
              >
                {isSubmitting ? "Submitting..." : "Plan My Visit"}
                {!isSubmitting && <ArrowRight size={18} />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlanVisit;
